import { Role } from '@/types/Role'
import { ComponentType, FunctionComponent, JSX, useEffect, useState } from 'react'
import { Post, PostType, Reaction, ReactionType, View } from '@/types/NewsFeed'
import styles from './post.module.scss'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import { IconButton, styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { red } from '@mui/material/colors'
import MediaContainer from './mediaContainer/mediaContainer'
import moment from 'moment-timezone'
import { useMutation } from '@apollo/client'
import { CREATE_REACTION, DELETE_REACTION } from '@/modules/API/news/news'
import { useTranslation } from 'react-i18next'
import Delete from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { TaskNoteStatus } from '@/types/TaskNote.ts'
import { Assignment, Call, CallMade, Description, EditNote, Event, PhoneDisabled, Voicemail } from '@mui/icons-material'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { useTimeDifference } from '@/hooks/useTimeDifference.ts'
import MessageWithURL from '@/components/_custom/MessageWithUrl'
import PollDisplay from './pollDisplay/pollDisplay'

export interface PostRowProps {
    post: Post
    role: Role
    daycareId: number
    handleModifyPost: (post: Post) => void
    handleDeletePost: (post: Post) => void
    canUpdate: boolean
    canValidPosts: boolean
    isApplication: boolean
}

export const NewsHtmlTooltip: ComponentType<TooltipProps> = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        border: '1px solid #dadde9',
        fontSize: 11,
        padding: 0
    }
}))

const PostRow: FunctionComponent<PostRowProps> = ({
    post,
    role,
    daycareId,
    handleModifyPost,
    handleDeletePost,
    canUpdate,
    canValidPosts,
    isApplication
}: PostRowProps) => {
    const { t } = useTranslation()

    const [isLoved, setIsLoved] = useState<boolean>(false)
    const [lovedId, setLovedId] = useState<number>()

    const difference: string | null = useTimeDifference(post.updateDate)

    const [createReaction] = useMutation(CREATE_REACTION)
    const [deleteReaction] = useMutation(DELETE_REACTION)

    useEffect(() => {
        if (post.reactions && post.reactions.length > 0) {
            post.reactions.forEach((reaction: Reaction): void => {
                if (reaction.reactionType === ReactionType.LOVE) {
                    setIsLoved(true)
                    setLovedId(reaction.id)
                }
            })
        }
    }, [post.reactions])

    function handleReaction(reactionType: ReactionType): void {
        if (reactionType === ReactionType.LOVE) {
            if (isLoved && lovedId) {
                deleteReaction({
                    variables: {
                        daycareId: daycareId,
                        reactionId: lovedId
                    }
                }).then(() => {
                    setIsLoved(!isLoved)
                    setLovedId(undefined)
                })
            } else {
                createReaction({
                    variables: {
                        daycareId: daycareId,
                        type: reactionType,
                        postId: post.id
                    }
                }).then((r) => {
                    setIsLoved(!isLoved)
                    setLovedId(r.data.createReaction ? r.data.createReaction : undefined)
                })
            }
        }
    }

    const isParent: boolean = role === Role.parent

    return (
        <div className={`${role === Role.parent && styles.PostParent} ${styles.Post}`}>
            <div className={styles.PostHeader}>
                <p className={styles.Date}> {difference} </p>
                {role !== Role.parent && (
                    <div className={styles.Views}>
                        <NewsHtmlTooltip
                            title={
                                <div className={styles.ReactionsList}>
                                    {post.views && post.views.length > 0
                                        ? [...post.views]
                                              .sort((a: View, b: View) => a.parentName.localeCompare(b.parentName))
                                              .map((view: View, index: number) => (
                                                  <div
                                                      className={styles.ViewContainer}
                                                      key={`${index}_${view.parentName}`}
                                                  >
                                                      <p className={styles.ViewName}>
                                                          {view.parentName.trim() === ''
                                                              ? t('unknown')
                                                              : view.parentName}
                                                      </p>
                                                      <p className={styles.ViewDate}>
                                                          {moment(view.creationDate, 'DD-MM-YYYY HH:mm:ss')
                                                              .utc(true)
                                                              .format('DD/MM/YYYY')}
                                                      </p>
                                                  </div>
                                              ))
                                        : t('noViews')}
                                </div>
                            }
                            arrow
                            placement='bottom'
                            enterTouchDelay={0}
                        >
                            <VisibilityIcon className={styles.ViewsIcon} />
                        </NewsHtmlTooltip>
                        {post.views?.length}
                    </div>
                )}
            </div>

            <div className={styles.Break}></div>

            {post.type === PostType.post ? (
                <div className={styles.PostBody}>
                    {post.message.trim() !== '' && <MessageWithURL message={post.message} />}
                    <MediaContainer
                        mediaList={post.medias}
                        isApplication={isApplication}
                        daycareId={daycareId}
                        postId={post.id}
                    />
                    {post.poll && <PollDisplay poll={post.poll} role={role} daycareId={daycareId} postId={post.id} />}
                </div>
            ) : (
                <div className={styles.PostBody}>
                    {post.taskNoteTitle && (
                        <>
                            <div className={styles.TitleContainer}>
                                {post.type === PostType.note ? (
                                    <EditNote className={isParent ? styles.IconParent : styles.IconDaycare} />
                                ) : (
                                    <Call className={isParent ? styles.IconParent : styles.IconDaycare} />
                                )}
                                <div className={styles.TextContainer}>
                                    <p className={styles.Title}>{post.taskNoteTitle}</p>
                                </div>
                            </div>
                            <div className={styles.BreakLine}></div>
                        </>
                    )}
                    {post.taskNoteStatus && (
                        <div className={styles.Container}>
                            {defineTaskNoteStatusIcon(post.taskNoteStatus, isParent)}
                            <p className={styles.Text}>{t(post.taskNoteStatus)}</p>
                        </div>
                    )}
                    {post.taskNoteDate && (
                        <div className={styles.Container}>
                            <Event className={isParent ? styles.IconParent : styles.IconDaycare} />
                            <p className={styles.Text}>
                                {format(
                                    parse(post.taskNoteDate, 'dd-MM-yyyy HH:mm', new Date()),
                                    'EEEE dd MMMM yyyy HH:mm',
                                    {
                                        locale: getLocalFromLanguage()
                                    }
                                )}
                            </p>
                        </div>
                    )}
                    {post.message && (
                        <div className={styles.Container}>
                            <Description className={isParent ? styles.IconParent : styles.IconDaycare} />
                            <MessageWithURL message={post.message} />
                        </div>
                    )}
                </div>
            )}

            {post.type === PostType.post && (
                <>
                    <div className={styles.Break}></div>
                    {role === Role.parent ? (
                        <div className={styles.PostFooterParent}>
                            <IconButton onClick={() => handleReaction(ReactionType.LOVE)}>
                                {isLoved ? <FavoriteIcon sx={{ color: red[500] }} /> : <FavoriteBorderIcon />}
                            </IconButton>
                        </div>
                    ) : (
                        <div className={styles.PostFooter}>
                            <div className={styles.PostFooter}>
                                <div className={styles.Like}>
                                    <NewsHtmlTooltip
                                        title={
                                            <div className={styles.ReactionsList}>
                                                {post.reactions && post.reactions.length > 0
                                                    ? [...post.reactions]
                                                          .sort((a: Reaction, b: Reaction) =>
                                                              a.parentName.localeCompare(b.parentName)
                                                          )
                                                          .map((reaction: Reaction, index: number) => (
                                                              <div key={`${index}_${reaction.id}`}>
                                                                  <p className={styles.ReactionName}>
                                                                      {reaction.parentName.trim() === ''
                                                                          ? t('unknown')
                                                                          : reaction.parentName}
                                                                  </p>
                                                              </div>
                                                          ))
                                                    : t('noReactions')}
                                            </div>
                                        }
                                        arrow
                                        placement='top'
                                        enterTouchDelay={0}
                                    >
                                        <FavoriteIcon className={styles.LikeIcon} />
                                    </NewsHtmlTooltip>
                                    {post.reactions?.length}
                                </div>
                                {canValidPosts && canUpdate && (
                                    <div className={styles.ModifyAndDelete}>
                                        <EditIcon className={styles.Icon} onClick={() => handleModifyPost(post)} />
                                        <Delete className={styles.Icon} onClick={() => handleDeletePost(post)} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default PostRow

function defineTaskNoteStatusIcon(status: TaskNoteStatus, isParent: boolean): JSX.Element {
    switch (status) {
        case TaskNoteStatus.leftMessage:
            return <Voicemail className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.noAnswer:
            return <PhoneDisabled className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.connected:
            return <CallMade className={isParent ? styles.IconParent : styles.IconDaycare} />
        default:
            return <Assignment className={isParent ? styles.IconParent : styles.IconDaycare} />
    }
}
